import { aircraftQuery } from "@common/api"
import type { AircraftPublic } from "@common/client"
import { useQuery } from "@tanstack/react-query"
import { useMemo } from "react"
import type { FieldValues, Path } from "react-hook-form"
import { type OptionGroup, SelectField } from "../fields/select"

type Props<T extends FieldValues> = {
  name: Path<T>
  label?: string
  className?: string
  optional?: boolean
}

function groupByModel(
  aircraft: AircraftPublic[],
): ReadonlyArray<OptionGroup<string>> {
  const byModel = new Map<string, AircraftPublic[]>()
  for (const item of aircraft) {
    const list = byModel.get(item.model) ?? []
    list.push(item)
    byModel.set(item.model, list)
  }
  return [...byModel.entries()]
    .map(([model, items]) => ({
      label: model,
      options: [...items]
        .sort((a, b) => a.registration_no.localeCompare(b.registration_no))
        .map((aircraft) => ({
          value: aircraft.id,
          label: aircraft.registration_no,
        })),
    }))
    .sort((a, b) => a.label.localeCompare(b.label))
}

export function AircraftField<T extends FieldValues>({
  name,
  label = "Aircraft",
  className,
  optional,
}: Props<T>) {
  const { data, isLoading } = useQuery(aircraftQuery({ limit: 1000 }))

  const groups = useMemo(() => groupByModel(data?.data ?? []), [data])

  return (
    <SelectField<T, string>
      name={name}
      label={label}
      className={className}
      disabled={isLoading}
      placeholder={isLoading ? "Loading…" : "Select aircraft"}
      optional={optional}
      groups={groups}
    />
  )
}
